<template>
  <a-modal
    :visible="visible"
    :confirm-loading="loading"
    :footer="null"
    @cancel="handleCancel"
  >
    <template #title>
      <div class="modal-title">환경 변수 등록</div>
    </template>
    
    <div class="modal-content">
      <a-form 
        ref="envConfigForm"
        :model="formState"
        :rules="rules"
        layout="vertical"
        :validate-messages="validateMessages"
      >
        <a-form-item 
          label="변수 타입" 
          name="envType"
          required
        >
          <a-select
            v-model:value="formState.envType"
            placeholder="변수 타입을 선택하세요"
          >
            <a-select-option value="MANUFACTURER_ID">제조사 아이디</a-select-option>
            <a-select-option value="KAKAO_TEMPLATE">카카오 알림톡 템플릿</a-select-option>
            <a-select-option value="GENERAL">일반</a-select-option>
          </a-select>
        </a-form-item>

        <a-form-item 
          label="환경 변수 키" 
          name="configKey"
          required
        >
          <a-input 
            v-model:value="formState.configKey"
            placeholder="환경 변수 키를 입력하세요"
          />
        </a-form-item>

        <a-form-item 
          label="환경 변수 값" 
          name="configValue"
          required
        >
          <a-input 
            v-model:value="formState.configValue"
            placeholder="환경 변수 값을 입력하세요"
          />
        </a-form-item>

        <a-form-item name="isSecret">
          <a-checkbox 
            v-model:checked="formState.isSecret"
            @change="handleSecretChange"
          >
            민감 정보로 설정
          </a-checkbox>
        </a-form-item>

        <a-form-item 
          label="설명" 
          name="desc"
        >
          <a-textarea
            v-model:value="formState.desc"
            placeholder="환경 변수에 대한 설명을 입력하세요"
            :rows="3"
          />
        </a-form-item>
      </a-form>
      
      <div class="modal-footer">
        <a-button @click="handleCancel">취소</a-button>
        <a-button 
          type="primary" 
          :loading="loading" 
          @click="handleSubmit"
        >확인</a-button>
      </div>
    </div>
  </a-modal>
</template>

<script>
import { defineComponent } from 'vue';
import environmentsApis from '../api';

export default defineComponent({
  name: 'CreateEnvConfigModal',

  props: {
    visible: {
      type: Boolean,
      required: true
    }
  },

  data() {
    return {
      loading: false,
      formState: {
        configKey: '',
        configValue: '',
        isSecret: false,
        envType: 'GENERAL',
        desc: ''
      },
      rules: {
        envType: [
          { required: true, message: '변수 타입을 선택해주세요' }
        ],
        configKey: [
          { required: true, message: '환경 변수 키를 입력해주세요' },
          { max: 100, message: '최대 100자까지 입력 가능합니다' }
        ],
        configValue: [
          { required: true, message: '환경 변수 값을 입력해주세요' }
        ],
        desc: [
          { max: 500, message: '최대 500자까지 입력 가능합니다' }
        ]
      },
      validateMessages: {
        required: '${label}을(를) 입력해주세요',
        types: {
          string: '올바른 ${label} 형식이 아닙니다'
        },
        string: {
          max: '${label}은(는) ${max}자를 초과할 수 없습니다'
        }
      },
      api: null
    }
  },

  created() {
    this.api = environmentsApis(this.$apiInstance, this.$store);
  },

  methods: {
    resetForm() {
      this.formState = {
        configKey: '',
        configValue: '',
        isSecret: false,
        envType: 'GENERAL',
        desc: ''
      };
      this.$refs.envConfigForm.resetFields();
    },

    handleSecretChange(e) {
      this.formState.isSecret = e.target.checked;
    },

    async validateForm() {
      try {
        await this.$refs.envConfigForm.validate();
        return true;
      } catch (error) {
        return false;
      }
    },

    async handleSubmit() {
      try {
        const isValid = await this.validateForm();
        if (!isValid) return;

        this.loading = true;
        
        await this.api.createEnvConfig(
          this.formState,
          () => {
            this.$emit('success');
            this.$emit('update:visible', false);
            this.resetForm();
          },
          (error) => {
            console.error('Failed to create config:', error);
            const errorMessage = error.response?.data?.message || '환경 변수 등록에 실패했습니다.';
            this.$message.error(errorMessage);
            throw error;
          }
        );
      } catch (error) {
        console.error('Failed to create config:', error);
      } finally {
        this.loading = false;
      }
    },

    handleCancel() {
      this.$emit('update:visible', false);
      this.resetForm();
    }
  }
});
</script>

<style lang="scss" scoped>
.modal-title {
  font-size: 16px;
  font-weight: bold;
}

.modal-content {
  padding: 20px;
  border: none;
  background: #fff;

  .ant-form-item {
    margin-bottom: 24px;

    &:last-child {
      margin-bottom: 0;
    }
  }
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 24px;
}

:deep(.ant-modal) {
  padding-bottom: 0;
  
  .ant-modal-content {
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);

    .ant-modal-close {
      color: rgba(0, 0, 0, 0.45);
    }

    .ant-modal-header {
      padding: 16px 20px;
      border: none;
      background: #fff;
      margin: 0;
    }

    .ant-modal-body {
      padding: 0;
      background: #fff;
      border: none;
    }

    .ant-modal-header,
    .ant-modal-body,
    .ant-modal-footer {
      border: none;
    }
  }
}
</style> 